/**
 * Handmatig vastgestelde routetarieven. Dit is de enige plek waar zulke
 * bedragen staan; `tariffs.ts` gebruikt ze en gaat daarmee vóór de
 * kilometerformule. Pagina's, calculator, betaalpagina en bevestiging halen
 * hun bedrag allemaal via `tariffs.ts` op, dus er bestaat geen tweede versie.
 *
 * Het tarief geldt voor 1 t/m 4 personen (gewone taxi). Waar hieronder ook een
 * vaste busprijs staat, gebruikt de prijsengine die voor 5 t/m 8 personen;
 * anders geldt de normale bustoeslag.
 */

import type { AnchorKey } from "./local-seo";

/** Vaste prijzen per plaats-slug naar een bestemming (anchor). */
export const FIXED_ANCHOR_PRICES: Record<string, Partial<Record<AnchorKey, number>>> = {
  barendrecht: {
    schiphol: 100,
    "rotterdam-centraal": 50,
    rotterdam: 50,
    "den-haag-centraal": 80,
    "den-haag": 80,
    breda: 80,
    "eindhoven-airport": 140,
  },
  "hoogvliet-rotterdam": {
    schiphol: 100,
    "rotterdam-centraal": 50,
    "den-haag-centraal": 80,
    "den-haag": 80,
    "eindhoven-airport": 140,
    breda: 80,
    rotterdam: 50,
  },
  ridderkerk: {
    schiphol: 100,
    "rotterdam-centraal": 50,
    rotterdam: 50,
    "den-haag-centraal": 80,
    "den-haag": 80,
    "eindhoven-airport": 140,
    breda: 80,
  },
  poortugaal: {
    schiphol: 90,
    "rotterdam-centraal": 50,
    rotterdam: 50,
    "den-haag-centraal": 80,
    "den-haag": 80,
    "eindhoven-airport": 140,
  },
  rhoon: {
    schiphol: 90,
    "rotterdam-centraal": 50,
    rotterdam: 50,
    "den-haag-centraal": 80,
    "den-haag": 80,
    "eindhoven-airport": 140,
  },
  spijkenisse: {
    schiphol: 100,
    "rotterdam-centraal": 60,
    rotterdam: 60,
    "den-haag-centraal": 80,
    "den-haag": 80,
    "eindhoven-airport": 150,
  },
};

/**
 * Vaste busprijzen (5 t/m 8 personen) voor dezelfde routes. Staat hier een
 * bedrag, dan geldt dat in plaats van vaste prijs + standaard bustoeslag.
 */
export const FIXED_BUS_PRICES: Record<string, Partial<Record<AnchorKey, number>>> = {
  barendrecht: {
    schiphol: 135,
    "rotterdam-centraal": 70,
    rotterdam: 70,
    "den-haag-centraal": 115,
    "den-haag": 115,
    breda: 115,
    "eindhoven-airport": 180,
  },
  "hoogvliet-rotterdam": {
    schiphol: 135,
    "rotterdam-centraal": 70,
    "den-haag-centraal": 115,
    "den-haag": 115,
    "eindhoven-airport": 180,
    breda: 115,
    rotterdam: 70,
  },
  ridderkerk: {
    schiphol: 135,
    "rotterdam-centraal": 70,
    rotterdam: 70,
    "den-haag-centraal": 115,
    "den-haag": 115,
    "eindhoven-airport": 180,
    breda: 115,
  },
  poortugaal: {
    schiphol: 125,
    "rotterdam-centraal": 65,
    rotterdam: 65,
    "den-haag-centraal": 115,
    "den-haag": 115,
    "eindhoven-airport": 185,
  },
  rhoon: {
    schiphol: 125,
    "rotterdam-centraal": 65,
    rotterdam: 65,
    "den-haag-centraal": 115,
    "den-haag": 115,
    "eindhoven-airport": 185,
  },
  spijkenisse: {
    schiphol: 135,
    "rotterdam-centraal": 80,
    rotterdam: 80,
    "den-haag-centraal": 115,
    "den-haag": 115,
    "eindhoven-airport": 190,
  },
};

/**
 * Extra bedrag voor een busje op een route met vaste auto- én busprijs
 * (beide richtingen), of `null` wanneer de standaard bustoeslag geldt.
 */
export function fixedBusExtra(aSlug: string | null | undefined, bSlug: string | null | undefined): number | null {
  if (!aSlug || !bSlug) return null;
  for (const [x, y] of [[aSlug, bSlug], [bSlug, aSlug]] as const) {
    const bus = FIXED_BUS_PRICES[x]?.[y as AnchorKey];
    const car = FIXED_ANCHOR_PRICES[x]?.[y as AnchorKey];
    if (bus != null && car != null) return bus - car;
  }
  return null;
}

/** Bestemmingen met een vaste prijs voor deze plaats, buiten de standaardlijst. */
export function extraFixedAnchors(slug: string, exclude: AnchorKey[] = []): AnchorKey[] {
  const table = FIXED_ANCHOR_PRICES[slug];
  if (!table) return [];
  return (Object.keys(table) as AnchorKey[]).filter((key) => !exclude.includes(key));
}
